import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Telemetry } from '@/lib/telemetry'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * Error boundary around the routed tree.
 *
 * It exists mainly for one failure mode that route-level code splitting
 * introduces: a chunk that no longer exists on the server.
 *
 * A visitor keeps a tab open, we deploy (the daily dev→main promote makes
 * that routine), and the hashed chunk filenames change. The old tab then
 * navigates and asks for `/assets/ExplorerPage-<oldhash>.js`, which is gone.
 * It does NOT 404 — `vercel.json`'s catch-all rewrite serves `index.html`
 * for any unmatched path, so the browser gets `text/html` where it expected
 * a module, rejects it on the MIME check, and `React.lazy` throws during
 * render. Without a boundary, React unmounts the whole tree: a permanent
 * white page that in-app navigation cannot recover from.
 *
 * A reload fixes it every time, because the freshly-fetched `index.html`
 * points at the new hashes. So the fallback's job is simply to say so and
 * offer the button.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Telemetry.errorSwallowed({
      error_type: error.name || 'RouteRenderError',
      operation: 'route_render',
      screen_name: window.location.pathname,
      error_message: `${error.message} | ${info.componentStack?.split('\n')[1]?.trim() ?? ''}`,
    })
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center">
        <h1 className="text-xl font-semibold text-brand-charcoal">
          This page needs a refresh
        </h1>
        <p className="mt-2 max-w-md text-sm text-slate-600 leading-relaxed">
          CTDCommons was updated while this tab was open, so part of the app
          could not be loaded. Reloading picks up the new version.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 px-5 py-2.5 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors"
        >
          Reload
        </button>
      </div>
    )
  }
}
